/**
 * `Halftone` — a printed dot field. `docs/design-direction.md` §6.2.
 *
 * A `radial-gradient` dot screen rotated 15° behind its content, masked to fade out
 * downwards, at 30% (`coarse`: a wider screen in `--accent-quiet` at 22%). It is the
 * device that makes a dark ground read as ink on paper rather than as a dark theme,
 * and it is the only "background texture" in the system.
 *
 * Where it is licensed (§6.2): behind the Now Playing header, under sticky bars,
 * inside the overlay scrim, behind the `--fs-64` archive numeral, and dragging 8px
 * under a rewind-to-refresh pull (§7.2). **Never as full-page wallpaper**, and
 * **never animated** — the field is a static background, because a moving screen over
 * scrolling content is exactly the compositing cost this direction cannot afford
 * (§10.2.5).
 *
 * The recipe (`.halftone` / `.halftone--coarse` in `styles/texture.css`) draws into a
 * `z-index: -1` pseudo-element inside an `isolation: isolate` container, so it never
 * paints over content and never joins the blend-mode budget of §6.13 — it has no
 * blend mode at all. `html[data-grain="off"]` strips it with the rest of the
 * atmosphere.
 */

import type { JSX, ReactNode } from 'react';

import { cx } from './Rule';

export interface HalftoneProps {
  readonly children?: ReactNode;
  /** The coarse 6px screen in `--accent-quiet`, for paper shading and scrims. */
  readonly coarse?: boolean;
  /** Element to render. Default `div`; `header` and `section` are the usual hosts. */
  readonly as?: 'div' | 'header' | 'section' | 'span';
  readonly className?: string | undefined;
}

export function Halftone({
  children,
  coarse = false,
  as = 'div',
  className,
}: HalftoneProps): JSX.Element {
  const Tag = as;
  return (
    <Tag className={cx('halftone', coarse && 'halftone--coarse', className)} data-halftone="true">
      {children}
    </Tag>
  );
}
